import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkle, CircleNotch, Tag } from "@phosphor-icons/react";
import { toast } from "sonner";
import ThemeToggle from "@/components/ThemeToggle";
import Logomark from "@/components/Logomark";
import UserMenu from "@/components/UserMenu";
import { PLAN_TYPES } from "@/lib/planiq";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Surface the plan types on the landing page, in a sensible reading order.
const PLAN_ORDER = ["thirty_sixty_ninety", "tactical_sprint", "macro_vision"];
const PLAN_TYPE_CARDS = PLAN_ORDER
  .map((v) => PLAN_TYPES.find((t) => t.value === v))
  .filter(Boolean);

const GoogleMark = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

// Mini "plan-on-a-page" motif (3 horizons, pillar-coded cards) — echoes the real product.
const PREVIEW = [
  { label: "Days 1\u201330", cards: ["#002FA7", "#C8102E", "#B45309"] },
  { label: "Days 31\u201360", cards: ["#1B4D3E", "#C8102E"] },
  { label: "Days 61\u201390", cards: ["#B45309", "#002FA7", "#1B4D3E"] },
];

const STEPS = [
  { n: "01", t: "Add your context", d: "Paste in your notes, or drop in documents." },
  { n: "02", t: "Calibrate the architecture", d: "Audit the AI-inferred constraints and strategic pillars to lock your custom framework." },
  { n: "03", t: "Track & export", d: "Work the board, then export a polished PDF or DOCX." },
];

export default function Hero() {
  const navigate = useNavigate();
  const { user, loginWithGoogle } = useAuth();
  const [sampleOpen, setSampleOpen] = useState(false);
  const [sampleLoading, setSampleLoading] = useState(false);

  const openSample = async () => {
    setSampleLoading(true);
    try {
      const plan = await api.createSample();
      setSampleOpen(false);
      navigate(`/plan/${plan._id || plan.id}`);
    } catch (e) {
      toast.error("Couldn't load the sample. Please try again.");
    } finally {
      setSampleLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="hero-page">
      {/* top bar */}
      <header className="border-b border-gray-200 px-6 sm:px-10 py-4 flex items-center justify-between">
        <span className="flex items-center gap-2">
          <Logomark size={24} />
          <span className="font-display font-black text-2xl tracking-tight">Xartis.</span>
          <span className="font-display font-medium text-2xl tracking-tight text-gray-400">Plan anything.</span>
        </span>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/pricing")} data-testid="hero-pricing-link"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-gray-900 transition-colors">
            <Tag size={16} weight="duotone" /> Pricing
          </button>
          {user ? (
            <UserMenu />
          ) : (
            <button onClick={loginWithGoogle} data-testid="hero-signin-link"
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-gray-900 transition-colors">
              Sign in
            </button>
          )}
          <ThemeToggle />
        </div>
      </header>

      {/* hero */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 sm:px-10 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-14 lg:py-0">
        {/* left — message + sign-in */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <p data-testid="hero-name-gloss" className="font-mono text-xs text-gray-400 border-l-2 border-gray-200 pl-3 leading-relaxed mb-5">
            <span className="font-semibold text-gray-700">Xartis</span>
            <span className="text-gray-500"> · "KAR-tiss"</span>
            <br />
            from the Greek <span lang="el" className="text-gray-600">χάρτης</span> — a map or chart.
          </p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
            Raw context in.
            <br />
            A clear plan out.
          </h1>
          <p className="mt-6 text-base sm:text-lg text-gray-600 leading-relaxed max-w-xl">
            Xartis turns whatever you're juggling into a clear, step-by-step plan —
            organised by focus areas and phases. A live board to track your progress,
            and a polished document to share.
          </p>

          {/* sign-in / authenticated entry */}
          {user ? (
            <div className="mt-9 max-w-sm space-y-3" data-testid="hero-authed-cta">
              <p className="text-sm text-gray-600">Signed in as <span className="font-semibold text-gray-900">{user.name || user.email}</span>.</p>
              <button onClick={() => navigate("/library")} data-testid="hero-go-library"
                className="w-full flex items-center justify-center gap-2 bg-gray-950 hover:bg-gray-700 text-white px-5 py-3 text-sm font-semibold transition-colors">
                Go to your plans <ArrowRight size={15} weight="bold" />
              </button>
              <button onClick={() => navigate("/start")} data-testid="hero-new-plan"
                className="w-full flex items-center justify-center gap-2 border border-gray-300 px-5 py-3 text-sm font-semibold text-gray-800 hover:border-gray-900 transition-colors">
                Start a new plan
              </button>
            </div>
          ) : (
            <div className="mt-9 max-w-sm space-y-3">
              <button onClick={loginWithGoogle} data-testid="sso-google-button"
                className="w-full flex items-center justify-center gap-3 border border-gray-300 bg-white px-5 py-3 text-sm font-semibold text-gray-800 hover:border-gray-900 hover:bg-gray-50 transition-colors">
                <GoogleMark /> Continue with Google
              </button>
              <p className="font-mono text-[11px] text-gray-400">
                Sign in to build, refine and export your own plans.
              </p>
            </div>
          )}
          <button onClick={() => setSampleOpen(true)} data-testid="hero-sample-button"
            className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-gray-900 border-b-2 border-gray-900 pb-0.5 hover:gap-3 transition-all">
            <Sparkle size={15} weight="fill" className="text-amber-500" />
            See a sample plan — no signup
          </button>
        </motion.div>

        {/* right — product motif */}
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="hidden lg:block"
        >
          <div className="border border-gray-200 bg-gray-50 p-4">
            <div className="flex items-center justify-between px-1 pb-3 mb-1 border-b border-gray-200">
              <span className="font-display font-bold text-sm tracking-tight">Plan on a Page</span>
              <span className="font-mono text-[10px] uppercase tracking-wider text-gray-400">Onboarding · 30-60-90</span>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {PREVIEW.map((col, ci) => (
                <div key={col.label} className="bg-white border border-gray-200 p-2.5 min-h-[230px]">
                  <p className="font-mono text-[9px] uppercase tracking-wider text-gray-400 mb-2.5">{col.label}</p>
                  <div className="space-y-2.5">
                    {col.cards.map((hex, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.35, delay: 0.3 + ci * 0.08 + i * 0.06 }}
                        className="border border-gray-200 border-l-[3px] bg-white p-2"
                        style={{ borderLeftColor: hex }}
                      >
                        <div className="h-1.5 w-2/3 rounded-sm" style={{ backgroundColor: hex, opacity: 0.85 }} />
                        <div className="mt-1.5 h-1 w-full bg-gray-100" />
                        <div className="mt-1 h-1 w-4/5 bg-gray-100" />
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </main>

      {/* plan types — what you can build */}
      <section className="border-t border-gray-200" data-testid="hero-plan-types">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-14 lg:py-16">
          <p className="font-mono text-xs tracking-[0.3em] uppercase text-gray-500 mb-3">Choose your horizon</p>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight leading-tight">
            Three ways to shape a plan
          </h2>
          <p className="mt-3 text-base text-gray-600 leading-relaxed max-w-2xl">
            Pick the time horizon that fits the work. Xartis extracts the underlying strategic
            constraints, establishes your focus areas, and builds a phased execution board dynamically.
          </p>
          <div className="mt-10 grid sm:grid-cols-3 gap-5">
            {PLAN_TYPE_CARDS.map((t, i) => (
              <motion.button
                key={t.value}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                onClick={() => navigate("/pricing")}
                data-testid={`plan-type-card-${t.value}`}
                className="group text-left border border-gray-200 bg-white p-6 hover:border-gray-900 transition-colors">
                <div className="h-1 w-12 mb-5" style={{ backgroundColor: t.accent }} />
                <div className="flex items-center justify-between gap-2">
                  <h3 className="font-display font-bold text-xl tracking-tight">{t.name}</h3>
                  <span className="shrink-0 font-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-gray-200 text-gray-500">{t.tag}</span>
                </div>
                <p className="font-mono text-xs text-gray-400 mt-2">{t.span}</p>
                <p className="text-sm text-gray-600 leading-relaxed mt-4">{t.blurb}</p>
                <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-900 mt-5 group-hover:gap-2.5 transition-all">
                  Choose your plan <ArrowRight size={15} weight="bold" />
                </span>
              </motion.button>
            ))}
          </div>
        </div>
      </section>

      {/* how it works */}
      <section className="border-t border-gray-200 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10 grid sm:grid-cols-3 gap-8">
          {STEPS.map((s) => (
            <div key={s.n} className="flex gap-4">
              <span className="font-mono text-sm text-gray-300 font-bold pt-0.5">{s.n}</span>
              <div>
                <h3 className="font-display font-bold text-base tracking-tight">{s.t}</h3>
                <p className="text-sm text-gray-500 leading-relaxed mt-1">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-gray-200 px-6 sm:px-10 py-4 flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-gray-400">Xartis · Plan anything</span>
        <span className="font-mono text-[11px] text-gray-300">© 2026</span>
      </footer>

      <AlertDialog open={sampleOpen} onOpenChange={(o) => !sampleLoading && setSampleOpen(o)}>
        <AlertDialogContent className="rounded-none" data-testid="sample-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Sparkle size={18} weight="fill" className="text-amber-500" />
              You're about to open a sample plan
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3 text-sm leading-relaxed">
              <span className="block">
                This is a ready-made example — a 90-day plan for <b>opening a neighbourhood coffee shop</b> —
                so you can explore the board and the document export right away.
              </span>
              <span className="block bg-amber-50 border border-amber-200 px-3 py-2.5 text-amber-900 text-[13px]">
                To keep this free for everyone, the sample is a <b>fixed example</b> — it wasn't generated
                from your own context and it uses <b>no AI credits</b>. Editing it with Atlas or
                re-generating the summary will still call the AI. When you're ready, start your own plan to
                put Xartis to work on your real situation.
              </span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" data-testid="sample-cancel" disabled={sampleLoading}>
              Maybe later
            </AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); openSample(); }}
              data-testid="sample-confirm" disabled={sampleLoading}
              className="rounded-none bg-gray-950 hover:bg-gray-700">
              {sampleLoading ? <CircleNotch size={16} className="animate-spin mr-1.5" /> : <Sparkle size={15} weight="fill" className="mr-1.5" />}
              Open the sample
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
